import { Table } from '@heroui/react'

import { RowActions } from '@/components/ui/row-actions'

import type { PosTable } from '../types'
import { branchLabel } from './pos-format'

interface TablesSetupTableProps {
  tables: PosTable[]
  onEdit: (table: PosTable) => void
  onDelete: (table: PosTable) => void
  busyIds?: string[]
}

/**
 * The floor plan as a list — what is where, and how many it seats.
 *
 * Deliberately says nothing about who is sitting at a table. That is the grid's
 * job, and a setup screen that also reported occupancy would be a second, worse
 * copy of it that somebody eventually edits while a party is mid-meal.
 */
export function TablesSetupTable({ tables, onEdit, onDelete, busyIds }: TablesSetupTableProps) {
  return (
    <Table>
      <Table.ScrollContainer>
        <Table.Content aria-label="Tables">
          <Table.Header>
            <Table.Column id="code" isRowHeader>
              Code
            </Table.Column>
            <Table.Column id="outlet">Outlet</Table.Column>
            <Table.Column id="zone">Zone</Table.Column>
            <Table.Column id="seats">Seats</Table.Column>
            <Table.Column id="actions">{/* Row menu */}</Table.Column>
          </Table.Header>

          <Table.Body>
            {tables.map((table) => (
              <Table.Row key={table.id} id={table.id}>
                <Table.Cell>
                  <span className="font-medium">{table.code}</span>
                </Table.Cell>

                <Table.Cell>
                  <span className="text-accent">{branchLabel(table.branch)}</span>
                </Table.Cell>

                <Table.Cell>
                  {table.zoneName ? (
                    <span className="text-accent">{table.zoneName}</span>
                  ) : (
                    /* Not a blank cell. An ungrouped table still appears on
                       the grid, under "Unzoned" — saying so here is what stops
                       somebody assuming it is missing from the floor. */
                    <span className="text-muted">Unzoned</span>
                  )}
                </Table.Cell>

                <Table.Cell>
                  <span className="tabular-nums">{table.seats}</span>
                </Table.Cell>

                <Table.Cell>
                  <RowActions
                    label={`table ${table.code}`}
                    isBusy={busyIds?.includes(table.id)}
                    actions={[
                      { id: 'edit', label: 'Edit' },
                      { id: 'delete', label: 'Remove' },
                    ]}
                    onAction={(id) => (id === 'edit' ? onEdit(table) : onDelete(table))}
                  />
                </Table.Cell>
              </Table.Row>
            ))}
          </Table.Body>
        </Table.Content>
      </Table.ScrollContainer>
    </Table>
  )
}
